/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

// tslint:disable: max-func-body-length

import { updateCustomerAttributes, updateCustomerAttributesInput } from '@msdyn365-commerce-modules/retail-actions';
import React, { useEffect, useState } from 'react';

import { IAccountLoyaltyViewProps } from '@msdyn365-commerce-modules/account-management';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { vSIInitiateRestCallAsync } from '../../../dotdigital-extension/DataActionExtension.g';
import {
    IAccountLoyaltyProps,
    IUpdatedMyAddressData,
    IUpdateMyDetailsLinkData,
    IClubRenewalCTAData
} from '../definition-extensions/account-loyalty.ext.props.autogenerated';
import { IAccountLoyaltyData } from './account-loyalty.data';
import LostYourCard from './custom-components/lost-your-card';
import CancelMembership from './custom-components/cancel-membership';
import { setDateFormat } from '../utilities/formatDate';

interface IErrorMessage {
    message: string | null;
    StatusCode: Number | null;
}
interface ICard {
    Number: string;
    Status: string;
    PIN: string;
}

interface IProgram {
    Id: number;
    Name: string;
}

interface IBalance {
    Type: string;
    Balance: string;
    Currency: string;
}

interface ITotalCredit {
    Type: string;
    Balance: string;
    Currency: string;
}

interface IAvailableReward {
    Id: number;
    ItemQuantityAvailable: number;
    ItemPrice?: number;
    ItemDiscountPercent?: number | null;
    StampsRequired?: boolean | null;
    StampBalance: number;
}

interface ILoyaltyAccount {
    Card: ICard;
    Program: IProgram;
    ProgramTier?: string | null;
    Customer?: {} | null;
    Balances: IBalance[];
    TotalCredits: ITotalCredit[];
    AvailableRewards: IAvailableReward[];
}

interface ICustomerRewardsInfo {
    Id: number;
    Status: string;
    Name: string;
    DisplayDescription: string | null;
    Display: string | null;
}

const enum ViewStateTypes {
    Details,
    CancelForm,
    LostYourCard
}

const AccountLoyaltyView: React.FC<IAccountLoyaltyViewProps & IAccountLoyaltyProps<IAccountLoyaltyData>> = props => {
    const {
        AccountLoyalty,
        heading,
        cardNumber,
        config,
        totalPoints,
        context: { actionContext }
    } = props;
    const {
        lostCardLabel,
        downloadAppStatement,
        updateInfo,
        freeTrialCancelledText,
        myDetailLabel,
        makeRoomLabel,
        moreLabel,
        upgradeLabel,
        homeStoreLabel,
        membershipExpiryLabel,
        cancelMembershipBtn,
        lostCardSubmittedStatement
    } = props.resources;
    const {
        pointsText,
        youGotFirstText,
        nameLabel,
        cardLabel,
        tierLabel,
        coffeeIntroText,
        coffeePointsText,
        drinksLeftText,
        requestNewCardText
    } = props.resources;
    const { localStoreAttribute } = props.config;
    //@ts-ignore
    const [coffeePerksBalance, setCoffeePerksBalance] = useState<number | undefined>(undefined);
    const [viewState, setViewState] = useState<ViewStateTypes>(ViewStateTypes.Details);
    const [activeTier, setActiveTier] = React.useState<string>('');
    const [loyaltyExpiryDate, setLoyaltyExpirydate] = React.useState<string>('');
    const [customerDDAttribute, setcustomerDDAttribute] = React.useState<string>('');
    const [loyaltyRemainingDays, setLoyaltyRemainingDays] = React.useState<number>(0);
    // const [showCancel, setShowCancel] = React.useState(false);
    // const [cancelMembership, setCancelMembership] = React.useState(false);
    //@ts-ignore
    const [coffeeMembershipError, setCoffeeMembershipError] = React.useState(false);
    const [displayLostCard, setDisplayLostCard] = React.useState(false);
    const [isDisabled, setIsDisabled] = React.useState(false);
    const [attriVal, setAttriVal] = React.useState('');
    const [canSubCheck, setCanSubCheck] = React.useState(false);

    /* VSI-Customization -- START If user is not authenticated route him to signin page  */
    if (!props.context.request.user.isAuthenticated) {
        const signinURL = props.context.request.user.signInUrl;
        window.location.assign(signinURL!);
    }
    /* VSI-Customization -- END  */

    useEffect(() => {
        if (!coffeePerksBalance) {
            // First call to get customer rewards if any
            const customerCardNo = props.data.loyaltyCard.result?.CardNumber;
            if (customerCardNo && config.programID) {
                _getLoyaltyInfo(
                    actionContext,
                    customerCardNo,
                    config.programID,
                    config.rewardID!,
                    config.availableRewardID!,
                    config.extraAvailableRewardID!,
                    setCoffeePerksBalance,
                    setCoffeeMembershipError
                ).catch(error => error);
            }
        }
    }, []);
    useEffect(() => {
        const loyaltGroups = props.data.loyaltyCard.result?.LoyaltyGroups![0];
        if (loyaltGroups) {
            const highestActiveLoyaltyCardTier = loyaltGroups.HighestActiveLoyaltyCardTier;

            const activatedTier = loyaltGroups.LoyaltyCardTiers!.find(card => card.RecordId === highestActiveLoyaltyCardTier);
            const loyaltyGroupTier = loyaltGroups.LoyaltyCardTiers?.find(tier => {
                return tier.TierId === 'Club Plus';
            });

            if (activatedTier) {
                //@ts-ignore
                setActiveTier(activatedTier.TierId);
            }

            if (loyaltyGroupTier) {
                const validToDate = loyaltyGroupTier.ValidTo;

                const endDate = new Date(validToDate ?? '');
                const today = new Date();
                const timeDifference = endDate.getTime() - today.getTime() + 24 * 60 * 60 * 1000;
                const remainingDays = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

                if (remainingDays) {
                    setLoyaltyRemainingDays(remainingDays);
                }
                const formattedDate = setDateFormat(endDate.toLocaleDateString('en-GB'));
                formattedDate && setLoyaltyExpirydate(formattedDate);
            }
        }

        getCustomerDDAttribute();
    }, []);

    useEffect(() => {
        const customer = props.data?.customerInformation.result!;
        const customerAttr = customer.Attributes;
        customerAttr?.map(attr => {
            let attrValue = attr.AttributeValue;

            if (attr.Name === 'Direct Debit' && attr.AttributeValue!.StringValue) {
                setAttriVal(attr.AttributeValue!.StringValue);
            }

            if (attr.Name === 'LostCardDate' && attrValue?.StringValue) {
                const lostCardDate = new Date(attrValue?.StringValue);
                const todayDate = new Date();
                const Difference_In_Time = todayDate.getTime() - lostCardDate.getTime();
                const Difference_In_Days = Difference_In_Time / (1000 * 3600 * 24);
                if (Difference_In_Days < 10) {
                    setDisplayLostCard(true);
                }
            }
        });
        checkRemDays();
    }, []);

    const tierId: string | undefined = getTierId(props);
    console.log(tierId, 'TIER ID');

    let loyaltyClass = 'ms-loyalty-free';
    if (tierId?.toLowerCase() === config.clubTierFreeText?.toLowerCase()) {
        loyaltyClass = 'ms-loyalty-free';
    } else if (tierId?.toLowerCase() === config.clubTierPlusText?.toLowerCase()) {
        loyaltyClass = 'ms-loyalty-plus';
        // loyaltyClass = 'ms-loyalty-premium';
    } else if (tierId?.toLowerCase() === config.premiumClubPlusTierText?.toLowerCase()) {
        loyaltyClass = 'ms-loyalty-premium';
    }
    const customerName = props.data.customerInformation.result?.Name;
    const localStore = props.data.customerInformation.result?.Attributes?.find(
        x => x.Name?.toLowerCase() === localStoreAttribute?.toLowerCase()
    );
    const { updateMyDetailsLink, updatedMyAddress, cancelButton } = props.config;
    const selectedStore = localStore?.AttributeValue?.StringValue || 'None';
    let showCancelBtn = false;

    if (props.data.loyaltyCard.result?.LoyaltyGroups![0]) {
        const highestActiveLoyaltyCardTier = props.data.loyaltyCard?.result.LoyaltyGroups[0].HighestActiveLoyaltyCardTier;

        const activatedTier = props.data.loyaltyCard?.result.LoyaltyGroups[0].LoyaltyCardTiers!.find(
            card => card.RecordId === highestActiveLoyaltyCardTier
        );

        if (activatedTier) {
            const showBtn = activatedTier?.TierId !== 'Club';
            showCancelBtn = showBtn;
        }
    }
    const getCustomerDDAttribute = () => {
        const customer = props.data.customerInformation.result!;
        const customerAttr = customer?.Attributes?.filter((attr: any) => {
            return attr.Name === 'Direct Debit';
        });
        if (customerAttr && customerAttr.length) {
            //@ts-ignore
            setcustomerDDAttribute(customerAttr[0].AttributeValue?.StringValue);
        }
    };
    const handleCancelMembership = async () => {
        const customer = props.data.customerInformation.result!;
        const customerAttr = customer.Attributes;
        const newAttributes = customerAttr?.map(attr => {
            let attrValue = attr.AttributeValue;
            if (attr.Name === 'Direct Debit') {
                const value = attrValue?.StringValue === 'DDRP' ? 'DDCAN' : 'DDRP';
                attrValue = { ...attr.AttributeValue, StringValue: value };
            }
            return { ...attr, AttributeValue: attrValue };
        });

        const input = new updateCustomerAttributesInput(customer.AccountNumber, newAttributes || [], props.context.request.apiSettings);
        const cancelResponse = await updateCustomerAttributes(input, actionContext).catch();
        if (cancelResponse && cancelResponse?.Attributes![3].AttributeValue!.StringValue === 'DDCAN') {
            setCanSubCheck(true);
        }
    };

    const checkRemDays = () => {
        const enrollmentDate = props.data.loyaltyCard.result?.LoyaltyGroups![0].LoyaltyCardTiers!.filter(
            tier => tier.TierId === 'Club Plus Free Trial'
        );
        if (enrollmentDate && enrollmentDate.length > 0) {
            let validTo: any = enrollmentDate && new Date(enrollmentDate[0].ValidTo);
            validTo = new Date(validTo.setDate(validTo.getDate() - 2));
            const currentDate: Date = new Date();
            currentDate <= validTo ? setIsDisabled(false) : setIsDisabled(true);

            const endDate = new Date(enrollmentDate[0].ValidTo ?? '');
            const formattedDate = setDateFormat(endDate.toLocaleDateString('en-GB'));
            formattedDate && setLoyaltyExpirydate(formattedDate);
        }
    };

    const handleLostYourCartRequest = () => {};

    const handleGoBack = () => {
        setViewState(ViewStateTypes.Details);
    };

    const handleLostYourCard = () => {
        setDisplayLostCard(true);
    };

    let isLoyaltyinProgress = props.context.request.cookies.get(props.context.request.user.customerAccountNumber!);

    if (isLoyaltyinProgress.status === 'NOT_FOUND') {
        isLoyaltyinProgress = props.context.request.cookies.get('club_plus_purchased');
    }

    const renderDetails = () => {
        const daysLimit = props.context.app.config.clubPlusRenewalDays || 30;
        const showRenewMembershipButton =
            activeTier === 'Club Plus' &&
            loyaltyRemainingDays <= daysLimit &&
            (customerDDAttribute === 'None' || customerDDAttribute === 'DDCAN');
        const { clubRenewalCTA } = props.config;

        return (
            <div>
                {heading}
                <div className='upper membership-detail-boxes'>
                    <div className='membership-detail-boxes_perbox user-points'>
                        <p>{youGotFirstText}</p>
                        <p className='points'>
                            <p className='ms-account-loyalty__total-points'>{totalPoints && totalPoints.toFixed(0)}</p>
                        </p>
                        <p>{pointsText}</p>
                    </div>
                    <div className='membership-detail-boxes_perbox user-voucher'>
                        <div className='icon-holder icon-vouchers'>
                            <img
                                src='https://images-eu-prod.cms.commerce.dynamics.com/cms/api/rsbnfrmlpw/imageFileData/MDmwZX?pubver=1'
                                alt='voucher icon'
                            />
                        </div>
                        {getRemainingPointsMessage(props, totalPoints)}
                    </div>
                    {tierId?.toLowerCase() !== config.clubTierFreeText?.toLowerCase() && (
                        <div className='membership-detail-boxes_perbox drinks-permonth'>
                            <div className='icon-holder icon-cup'>
                                <img
                                    src='https://images-eu-prod.cms.commerce.dynamics.com/cms/api/rsbnfrmlpw/imageFileData/MDmzAU?pubver=1'
                                    alt='cup icon'
                                />
                            </div>
                            {coffeeMembershipError ? (
                                <p>{drinksLeftText}</p>
                            ) : (
                                <p>
                                    {coffeeIntroText} <b>{coffeePerksBalance}</b> {coffeePointsText}
                                </p>
                            )}
                        </div>
                    )}
                    <div className='membership-detail-boxes_perbox card-info'>
                        <h4>{myDetailLabel}</h4>
                        <p>
                            {nameLabel} {customerName}
                        </p>
                        <p>
                            {cardLabel} {cardNumber}
                        </p>
                        <p>
                            {tierLabel} {tierId}
                        </p>
                        {loyaltyExpiryDate && activeTier !== 'Club' && (
                            <p>
                                {membershipExpiryLabel} {loyaltyExpiryDate}
                            </p>
                        )}
                        <p>
                            {homeStoreLabel} {selectedStore}
                        </p>
                    </div>
                    {/* /** END -- Conditional renderig of divs */}
                    <div className='membership-detail-boxes_perbox update-your-info'>
                        <h4>{updateInfo}</h4>
                        <Node tag='div' className='content-section'>
                            &nbsp;
                        </Node>
                        <Node tag='div' className='link-holder'>
                            {showRenewMembershipButton ? _renderLinkButton(clubRenewalCTA, props) : <></>}
                            {_renderLinkButton(updateMyDetailsLink, props)}
                            {_renderLinkButton(updatedMyAddress, props)}
                            {/* Remove condition false for displaying cancel button */}
                            {false &&
                                showCancelBtn &&
                                // directDebitAttr?.AttributeValue?.StringValue === 'DDRP' &&
                                _renderLinkButton(cancelButton, props, () => setViewState(ViewStateTypes.CancelForm))}
                            {tierId == 'Club Plus Free Trial' &&
                                (attriVal !== '' && !(attriVal === 'DDCAN') && !canSubCheck ? (
                                    <button
                                        className='cta__btn'
                                        disabled={isDisabled}
                                        onClick={() => setViewState(ViewStateTypes.CancelForm)}
                                    >
                                        {cancelMembershipBtn}
                                    </button>
                                ) : (
                                    <p className='cancellation_text'>{freeTrialCancelledText}</p>
                                ))}
                        </Node>
                    </div>
                    <div className='membership-detail-boxes_perbox lost-your-card'>
                        <h4>{lostCardLabel}</h4>
                        <div className='link-holder'>
                            {/* Hidden Send Detail button */}
                            <button
                                disabled={displayLostCard}
                                className='cta__btn'
                                onClick={() => setViewState(ViewStateTypes.LostYourCard)}
                            >
                                {requestNewCardText}
                            </button>
                            {/* VSI-Customization -- START Render request new card link based on tierId  */}
                            {/* {renderRequestNewCardBasedOnTierId(props, tierId, () => setViewState(ViewStateTypes.LostYourCard))} */}
                            {/* VSI-Customization -- END  */}
                        </div>
                        <p>{displayLostCard ? lostCardSubmittedStatement : downloadAppStatement}</p>
                        <div className='app-buttons'>
                            <a
                                className='msc-cta__primary appstore'
                                role='button'
                                title='App Store'
                                href='https://apps.apple.com/gb/app/dobbies-garden-centres/id1547224723'
                                aria-label=''
                                target='_blank'
                            >
                                App Store
                            </a>
                            <a
                                className='msc-cta__primary googleplay'
                                role='button'
                                title='Google Play'
                                href='https://play.google.com/store/apps/details?id=com.dobbies.android&pli=1'
                                aria-label=''
                                target='_blank'
                            >
                                Google Play
                            </a>
                        </div>
                    </div>
                    {tierId && tierId.toLowerCase() === config.clubTierFreeText?.toLowerCase() ? (
                        <div className='membership-detail-boxes_perbox more-room'>
                            <div className='more-room__top'>
                                <div className='more-room__icon'>
                                    <img
                                        src='https://images-eu-prod.cms.commerce.dynamics.com/cms/api/rsbnfrmlpw/imageFileData/MDmwZU?pubver=1'
                                        alt='club plus logo'
                                    />
                                </div>
                                <div className='more-room__content'>
                                    <h4>{makeRoomLabel.charAt(0).toUpperCase() + makeRoomLabel.slice(1).toLowerCase()}</h4>
                                    <h2>{moreLabel?.toLowerCase()}</h2>
                                    <div className='more-room__content-bottom'>
                                        {isLoyaltyinProgress.value !== 'true' ? (
                                            <>
                                                <p>{upgradeLabel}</p>
                                                <div className='link-holder'>
                                                    <a className='cta__btn' href={props.config.upgradeNow?.linkUrl.destinationUrl}>
                                                        {props.config.upgradeNow?.linkText}
                                                    </a>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <p className='mt-2'>{props.config.clubPlusAlreadyPurchasedMsg}</p>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className='more-room__bottom'>
                                {isLoyaltyinProgress.value !== 'true' ? (
                                    <>
                                        <p>{upgradeLabel}</p>
                                        <div className='link-holder'>
                                            <a className='cta__btn' href={props.config.upgradeNow?.linkUrl.destinationUrl}>
                                                {props.config.upgradeNow?.linkText}
                                            </a>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <p className='mt-2'>{props.config.clubPlusAlreadyPurchasedMsg}</p>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        ''
                    )}
                </div>
            </div>
        );
    };

    const renderCurrentView = () => {
        switch (viewState) {
            case ViewStateTypes.Details:
                return renderDetails();
            case ViewStateTypes.CancelForm:
                return (
                    <CancelMembership
                        resources={props.resources}
                        onSubmit={handleCancelMembership}
                        onCancel={handleGoBack}
                        expireDate={
                            props.data.loyaltyCard.result
                                ?.LoyaltyGroups![0].LoyaltyCardTiers!.filter(tier => tier.TierId === 'Club Plus Free Trial')[0]
                                .ValidTo.toString() || ''
                        }
                        config={props.config}
                    />
                );
            case ViewStateTypes.LostYourCard:
                return (
                    <LostYourCard
                        data={props.data}
                        disableCardBtn={handleLostYourCard}
                        context={props.context}
                        cardNumber={props.data.loyaltyCard.result?.CardNumber}
                        userToken={props.context.request.user.isAuthenticated && props.context.request.user.token}
                        resources={props.resources}
                        onSubmit={handleLostYourCartRequest}
                        onCancel={handleGoBack}
                        tierId={tierId}
                        clubImage={props.config.clubCardImage?.src}
                        clubPlusImage={props.config.clubPlusCardImage?.src}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Module {...AccountLoyalty} className={loyaltyClass}>
            {renderCurrentView()}
        </Module>
    );
};
// Following function gets account loyalty account
// @ts-ignore
export const _getLoyaltyInfo = async (
    actionContext: Msdyn365.IActionContext,
    customerCardNo: string,
    programId: number,
    rewardId: number,
    availableRewardId: number,
    extraAvailableRewardID: number,
    setCoffeePerksBalance: React.Dispatch<React.SetStateAction<number | undefined>>,
    setCoffeeMembershipError: React.Dispatch<React.SetStateAction<boolean>>
) => {
    try {
        const customerRewardsInfo = await vSIInitiateRestCallAsync(
            { callerContext: actionContext },
            'Zonal',
            'get',
            "{'cache-control':'no-cache', 'Accept':'application/json'}",
            '',
            '',
            `/v1/api/programs/${programId}/rewards`
        );
        let coffeeRewardInfo: ICustomerRewardsInfo | undefined;
        let extraCoffeeRewardInfo: ICustomerRewardsInfo | undefined;
        if (customerRewardsInfo.result) {
            const rewardsInfo: ICustomerRewardsInfo[] = JSON.parse(customerRewardsInfo.result);
            coffeeRewardInfo = rewardsInfo.find(reward => reward.Id === rewardId);
            extraCoffeeRewardInfo = rewardsInfo.find(reward => reward.Id === extraAvailableRewardID);
            if (coffeeRewardInfo?.Status === 'Enabled') {
                const customerLoyaltyInfo = await vSIInitiateRestCallAsync(
                    { callerContext: actionContext },
                    'Zonal',
                    'get',
                    "{'cache-control':'no-cache', 'Accept':'application/json'}",
                    '',
                    '',
                    `v1/api/programs/${programId}/accounts/${customerCardNo}`
                );
                if (customerLoyaltyInfo.result) {
                    const responseError: IErrorMessage = JSON.parse(customerLoyaltyInfo.result);
                    if (
                        (responseError.StatusCode && responseError.StatusCode === 404) ||
                        (responseError.message && responseError.message.length > 0)
                    ) {
                        //  Error in getting token
                        setCoffeeMembershipError(true);
                        console.log('error in getting token');
                        // this.setState({ submissionErrorMsg: submissionErrorMessage, submissionSuccessMsg: undefined });
                        return;
                    } else {
                        const customerRewards: ILoyaltyAccount = JSON.parse(customerLoyaltyInfo.result);

                        // const coffeePerksReward = customerRewards.AvailableRewards.find(reward => reward.Id === 1532); // do not show this reward , only recurringrewardShow.
                        const coffeePerksRecurringReward = customerRewards.AvailableRewards.find(reward => reward.Id === availableRewardId);
                        const extraCoffeePerksReward = customerRewards.AvailableRewards.find(
                            reward => reward.Id === extraAvailableRewardID
                        );

                        const extraCoffeePerksRewardQuantity = extraCoffeePerksReward?.ItemQuantityAvailable ?? 0;

                        //const coffeePerkQuantity = coffeePerksReward?.ItemQuantityAvailable ?? 0;
                        let coffeePerkRecurringQuantity = coffeePerksRecurringReward?.ItemQuantityAvailable ?? 0;
                        if (extraCoffeeRewardInfo?.Status === 'Enabled') {
                            coffeePerkRecurringQuantity = coffeePerkRecurringQuantity + extraCoffeePerksRewardQuantity;
                        }
                        setCoffeePerksBalance(coffeePerkRecurringQuantity);
                        return;
                    }
                }
            } else {
                setCoffeePerksBalance(0);
            }
        }
    } catch (err) {
        setCoffeeMembershipError(true);
        console.log(err);
    }
};

// Customer

const _renderLinkButton = (
    link: IUpdateMyDetailsLinkData | IUpdatedMyAddressData | IClubRenewalCTAData | undefined,
    props: IAccountLoyaltyViewProps & IAccountLoyaltyProps<IAccountLoyaltyData>,
    onCancel?: () => void
) => {
    if (!link) {
        return null;
    }

    if (link.linkUrl.destinationUrl === '/dobbies/cancel') {
        return (
            <button onClick={onCancel} aria-label={link.ariaLabel} className='cta__btn'>
                {link.linkText}
            </button>
        );
    }
    const linkData: Msdyn365.ILinksData = {
        ariaLabel: link.ariaLabel,
        className: 'cta__btn',
        linkText: link.linkText,
        linkUrl: link.linkUrl.destinationUrl,
        openInNewTab: link.openInNewTab,
        role: 'button'
    };
    return <Msdyn365.Link link={linkData} editProps={{ requestContext: props.context.request }} />;
};

const getTierId = (props: IAccountLoyaltyViewProps & IAccountLoyaltyProps<IAccountLoyaltyData>) => {
    const loyaltyGroup = props.data.loyaltyCard.result?.LoyaltyGroups && props.data.loyaltyCard.result?.LoyaltyGroups[0];
    // highestLoyaltCardTier
    const highestActiveLoyaltyCardTier = loyaltyGroup && loyaltyGroup.HighestActiveLoyaltyCardTier;
    const loyaltyCardTiers = loyaltyGroup && loyaltyGroup.LoyaltyCardTiers;
    // Get TierId based on the active cardTier
    if (loyaltyCardTiers && loyaltyCardTiers?.length > 0) {
        for (const [_, loyaltyCardTier] of loyaltyCardTiers.entries()) {
            if (loyaltyCardTier.RecordId === highestActiveLoyaltyCardTier) {
                return loyaltyCardTier.TierId;
            }
        }
    }
    return;
};

const getRemainingPointsMessage = (props: IAccountLoyaltyViewProps & IAccountLoyaltyProps<IAccountLoyaltyData>, clientPoints: number) => {
    const { collectText, collectPointsText } = props.resources;
    const totalPointsQuota = props.config.totalPointsToGetVoucher || 150;
    const threshold = props.config.pointsThreshold || 50;
    const pointValue = props.config.pointValue || 0.5;
    const earnedPoints = clientPoints;

    // First calculate voucher value for one point
    const voucherForOnePoint = pointValue / threshold;
    /* The point calculation here is as following:
     * 150pts = £1.50 voucher
     * 200pts = £2 voucher
     * Each following 50pts adds £0.50 to their voucher. */

    if (earnedPoints < totalPointsQuota) {
        // user has less points than to get voucher
        // Now multiplies it by totalPointsQuota (total more points to be earned) to get voucher price and format its currency
        const voucherPrice = props.context.cultureFormatter.formatCurrency(
            voucherForOnePoint * totalPointsQuota,
            props.context.request.channel?.Currency
        );
        return (
            <p>
                {collectText} <b>{totalPointsQuota - earnedPoints}</b> {collectPointsText}
                <b> {voucherPrice}</b>
            </p>
        );
    } else if (earnedPoints >= totalPointsQuota) {
        // user has more points than minimum points required
        const remainder = earnedPoints % threshold;
        // Get value of the next voucher points issuing level from earnedPoints e.g. 200 is that value from 220 as 50 is multiple of 200
        let nextVoucherPoints = earnedPoints - remainder;
        // points needed to get to next voucher level
        nextVoucherPoints += threshold;
        // points needed for next voucher
        const pointsToCollect = threshold - remainder;

        // Now multiplies it by totalPointsQuota (total more points to be earned) to get voucher price and format its currency
        const voucherPrice = props.context.cultureFormatter.formatCurrency(
            voucherForOnePoint * nextVoucherPoints,
            props.context.request.channel?.Currency
        );
        return (
            <p>
                {collectText} <b>{pointsToCollect}</b> {collectPointsText} <b> {voucherPrice}</b>
            </p>
        );
    }
    return;
};

export default AccountLoyaltyView;
